import { css, DismissibleTag, RenderValueProps, styled } from 'canoe';
import has from 'lodash/has';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { stripNameDifferentiator } from 'dashboard/pages/Segment/components/DefinitionForm/nameDifferentiatorUtils';

import { DefaultItemProps, SelectProps } from './Select';
import { SelectField } from './SelectField';

const PillsWrapper = styled.div`
  ${({ theme }) => css`
    max-height: 400px;
    padding: ${theme.spaces.xxs} 0;
    display: flex;
    gap: ${theme.spaces.xxs};
    flex-wrap: wrap;
    overflow-y: auto;
  `}
`;

const parsePastedValues = (pastedContent: string) => {
  return pastedContent.split(/[,\s]+/g);
};

export interface MultiSelectProps<Item = DefaultItemProps, Values = any>
  extends Omit<SelectProps<Item, Values>, 'onChange' | 'isMultiselect' | 'allowCustomValues'> {
  currentValue: string | string[] | null;
  valueIsEmpty: boolean;
  valueIsArray: boolean;
  noAvailableOptions: boolean;
  getItems: (newOptions?: Item[]) => Item[];
}

export const MultiSelect = <Item extends DefaultItemProps, Values>({
  name,
  options,
  onSearch,
  onClearSearch,
  selectFirstByDefault,
  currentValue,
  valueIsArray,
  valueIsEmpty,
  noAvailableOptions,
  getItems,
  ...props
}: MultiSelectProps<Item, Values>) => {
  const { values, setFieldValue } = props;

  const initialMultiselectValue = useMemo(() => {
    if (valueIsEmpty) {
      return [];
    }

    if (!options.length) {
      return ((valueIsArray ? currentValue : [currentValue]) as string[]).map((value: string) => ({
        label: value,
        value,
      })) as Item[];
    }

    return ((valueIsArray ? currentValue : [currentValue]) as string[]).map((value: string) => {
      const foundItem = options.find((item) => item.value === value);
      return foundItem || ({ label: value, value } as Item);
    });
  }, []);

  const [selected, setSelected] = useState<Item[]>(initialMultiselectValue);

  const items = useMemo(() => {
    let newOptions;
    let customValues: string[] = [];

    if (currentValue) {
      const currentValues = (valueIsArray ? currentValue : [currentValue.toString()]) as string[];
      customValues = currentValues.filter(
        (value: string) => !options?.find((option) => option.value === value),
      );
    }

    if (customValues.length > 0) {
      newOptions = customValues.map((value: string) => ({
        label: value,
        value,
      })) as Item[];
    }

    return getItems(newOptions);
  }, [currentValue, options, valueIsArray]);

  const onSelect = useCallback(
    (item: Item, validate = true) => {
      if (!item) {
        setSelected([]);
        setFieldValue(name, null, validate);
        return;
      }

      setSelected((oldSelected) => {
        const indexOfId = oldSelected.findIndex((s) => s.value === item?.value);
        const newMultipleSelected =
          indexOfId > -1
            ? oldSelected.filter((s) => s.value !== item?.value)
            : [...oldSelected, item];

        setFieldValue(
          name,
          newMultipleSelected.length > 0 ? newMultipleSelected.map(({ value }) => value) : null,
          validate,
        );
        return newMultipleSelected;
      });
    },
    [name],
  );

  useEffect(() => {
    if (!noAvailableOptions && !has(values, name) && options?.length) {
      if (selectFirstByDefault && items[0]) {
        onSelect?.(items[0] as Item); // selecting by default should go the normal step of setting other form values besides this one
      } else {
        setFieldValue(name, null, false);
      }
    } else if (has(values, name)) {
      if (!valueIsArray && currentValue) {
        // if we are switching to multi-select dropdown,
        // make sure the value is converted to array
        setFieldValue(name, [currentValue.toString()], false);
      }
    }
  }, []);

  const onFilter = useCallback(
    (e) => {
      const newValue = e.target.value;

      if (e.nativeEvent.inputType === 'insertFromPaste') {
        // value has been pasted, parse accordingly
        const parsedValues = parsePastedValues(newValue);
        const currentValues = valueIsEmpty ? [] : currentValue;
        const newFieldValue = [
          ...parsedValues,
          ...(Array.isArray(currentValues) ? currentValues : [currentValues]).filter(
            (value: string) => !parsedValues.includes(value),
          ),
        ];

        const newSelected = selected.filter((item) => !parsedValues.includes(item.value));
        setSelected([
          ...newSelected,
          ...(parsedValues.map((value) => ({
            label: value,
            value,
          })) as Item[]),
        ]);
        setFieldValue(name, newFieldValue);
        onClearSearch?.();
      } else {
        onSearch?.(newValue);
      }
    },
    [currentValue, selected, onSearch],
  );

  const renderValue = useMemo(() => {
    return ({ selectedItems, ...selectedProps }: RenderValueProps<Item>) => (
      <PillsWrapper>
        {selectedItems?.map((selectedItem) => (
          <DismissibleTag
            {...selectedProps}
            key={selectedItem.value}
            variant="secondary"
            text={stripNameDifferentiator(selectedItem.label)}
            onDismiss={(e) => {
              e.stopPropagation();
              onSelect(selectedItem);
            }}
            limitWidth
          />
        ))}
      </PillsWrapper>
    );
  }, [onSelect]);

  return (
    <SelectField<Item>
      {...props}
      name={name}
      noAvailableOptions={noAvailableOptions}
      items={items}
      selectedItem={selected}
      isMultiselect
      renderValue={renderValue}
      onSelect={onSelect}
      onFilter={onSearch ? onFilter : undefined}
      onClearSearch={onClearSearch}
    />
  );
};
